import { shouldNotifyUser, shouldSendPushNotification } from './notificationPreferences';
import { supabase } from "@/integrations/supabase/client";
import { getUserEmail } from './getUserEmail';
import { sendPushNotification } from './oneSignalService';
import { createLogger } from '@/services/utils/loggingUtils';

const logger = createLogger('messageNotification');

// Keep track of recent notifications to prevent spamming
const notificationTracker: Record<string, number> = {};
const NOTIFICATION_COOLDOWN_MS = 1000 * 60 * 2; // 2 minutes cooldown

// Send notification for a new message
export const sendMessageNotification = async (
  recipientId: string,
  senderId: string,
  content: string,
  senderName: string,
  reagentName?: string
): Promise<boolean> => {
  try {
    console.log(`Starting message notification process to user ${recipientId} from ${senderId}`);
    
    // Check if user wants to be notified about messages
    const shouldNotify = await shouldNotifyUser(recipientId, "messages");
    if (!shouldNotify) {
      console.log(`User ${recipientId} has message notifications disabled`);
      return false;
    }
    
    console.log(`User ${recipientId} has message notifications enabled, proceeding...`);
    
    // Check cooldown to prevent spamming
    const now = Date.now();
    const key = `message:${recipientId}:${senderId}`;
    const lastNotification = notificationTracker[key] || 0;
    
    if (now - lastNotification < NOTIFICATION_COOLDOWN_MS) {
      console.log(`Skipping notification due to cooldown period for ${key}`);
      // Don't treat this as an error, just return true since we're skipping by design
      return true;
    }
    
    // Get recipient's email 
    const recipientEmail = await getUserEmail(recipientId);
    if (!recipientEmail) {
      console.error("Could not determine recipient email address");
      return false;
    }
    
    console.log(`Sending message notification to: ${recipientEmail}`);
    
    // Call the edge function using the Supabase SDK
    console.log("Preparing to call send-message-notification function with payload:", {
      recipientId,
      senderId,
      content,
      reagentName,
      senderName,
      recipientEmail
    });
    
    try {
      console.log("Using supabase.functions.invoke to call send-message-notification function");
      
      const { data, error } = await supabase.functions.invoke("send-message-notification", {
        body: {
          recipientId,
          senderId,
          content,
          reagentName,
          senderName,
          recipientEmail
        }
      });
      
      if (error) {
        console.error("Error calling send-message-notification function:", error);
        return false;
      }
      
      console.log("Message notification function response:", data);
      
      // Update the notification tracker only on successful send
      notificationTracker[key] = now;
      
      // Send push notification if user has push notifications enabled
      try {
        console.log("Checking if push notification should be sent for recipient:", recipientId);
        logger.debug("Checking if push notification should be sent");
        const shouldSendPush = await shouldSendPushNotification(recipientId, "messages");
        
        console.log("shouldSendPush result:", shouldSendPush);
        
        if (shouldSendPush) {
          console.log("Sending push notification for new message");
          logger.debug("Sending push notification for new message");
          const pushTitle = `New message from ${senderName}`;
          const pushMessage = reagentName 
            ? `About ${reagentName}: ${content.substring(0, 100)}${content.length > 100 ? '...' : ''}`
            : content.substring(0, 100) + (content.length > 100 ? '...' : '');
          
          const pushResult = await sendPushNotification(
            [recipientId],
            pushTitle,
            pushMessage,
            {
              type: 'message',
              senderId,
              reagentName
            }
          );
          
          console.log("Push notification result:", pushResult);
          logger.debug("Push notification sent successfully");
        } else {
          console.log("User has push message notifications disabled");
          logger.debug("User has push message notifications disabled");
        }
      } catch (pushError) {
        console.error("Error sending push notification (non-critical):", pushError);
        logger.error("Error sending push notification (non-critical):", pushError);
        // Don't fail the entire operation for push notification errors
      }
      
      console.log("Message notification email sent successfully or skipped due to cooldown");
      return true;
    } catch (functionError) {
      console.error("Exception calling edge function:", functionError);
      return false;
    }
  } catch (error) {
    console.error("Exception in sendMessageNotification:", error);
    return false;
  }
};
